import { asset } from '@/lib/paths.ts';

/**
 * Responsive <img> that emits a srcset over the width variants produced by
 * `bun run images` (<name>-480.webp, -960.webp, -1440.webp).
 *
 * Why this exists: the original shipped one 1920px-wide file to every device.
 * On a 390px phone the hero was a 266 KB download and a 1920x1280 decode,
 * which is what pushed mobile LCP past 5s. With srcset the same viewport now
 * takes the 22 KB variant.
 *
 * width/height are always emitted so the aspect ratio is reserved before the
 * image loads — that is what keeps CLS at 0.
 */
export interface ImgProps {
  /** Repo-relative path to the full-size source, e.g. "images/hero-home.webp". */
  src: string;
  alt: string;
  width: number;
  height: number;
  /** Omit for below-the-fold images (defaults to lazy). */
  priority?: boolean;
  /** `sizes` attribute; defaults to full viewport width. */
  sizes?: string;
  className?: string;
  /** Widths that exist on disk for this image. */
  variants?: readonly number[];
}

const DEFAULT_VARIANTS = [480, 960, 1440] as const;

export function Img({
  src,
  alt,
  width,
  height,
  priority = false,
  sizes = '100vw',
  className,
  variants = DEFAULT_VARIANTS,
}: ImgProps) {
  const base = src.replace(/\.webp$/, '');
  const usable = variants.filter((w) => w < width);

  const srcSet =
    usable.length > 0
      ? [
          ...usable.map((w) => `${asset(`${base}-${w}.webp`)} ${w}w`),
          `${asset(src)} ${width}w`,
        ].join(', ')
      : undefined;

  return (
    <img
      src={asset(src)}
      {...(srcSet ? { srcSet, sizes } : {})}
      alt={alt}
      width={width}
      height={height}
      // The LCP image must be eager and high-priority; everything else defers.
      loading={priority ? 'eager' : 'lazy'}
      fetchPriority={priority ? 'high' : 'auto'}
      decoding={priority ? 'sync' : 'async'}
      {...(className ? { className } : {})}
    />
  );
}
